import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../api/client';
import { Button, Chip, Icon, Note, Select, Skeleton, Empty } from '../ui';
const errText = (e: any, fb: string) => { try { return JSON.parse(e?.message).detail || fb; } catch { return e?.message || fb; } };

/** /units/bpay-mapping — Айл ↔ Төрийн банкны (BPay) хэрэглэгч холболт.
 *  Оршин суугч QR хуудсаар зөвхөн СӨХ-ийн админ холбосон хэрэглэгчийн нэхэмжлэхийг л харна. */

type Row = { unit_id: string; building: string; unit_number: string; door: string; owner_name: string; bpay_customer_id: string; bpay_customer_ref: string; bpay_customer_name: string; linked: boolean; bpay_soh_due: number; bpay_due_total: number; bpay_synced_at: string | null };
type Cand = { customer_id: string; customer_code: string; customer_name: string; address: string; name_match: boolean; taken_by: string | null };

export default function BpayMappingPage() {
  const navigate = useNavigate();
  const [data, setData] = useState<{ address: string; configured: boolean; buildings: string[]; items: Row[]; linked: number; total: number } | null>(null);
  const [building, setBuilding] = useState('');
  const [filter, setFilter] = useState<'all' | 'unlinked' | 'linked'>('all');
  const [open, setOpen] = useState<string | null>(null);
  const [cands, setCands] = useState<Record<string, Cand[] | 'loading' | { error: string }>>({});
  const [busy, setBusy] = useState<string | null>(null);
  const [auto, setAuto] = useState<{ running: boolean; linked: number; ambiguous: number; notfound: number; remaining: number } | null>(null);
  const [msg, setMsg] = useState('');
  const [sync, setSync] = useState<{ running: boolean; synced: number; remaining: number; failed: number } | null>(null);
  const runSync = async () => {
    setSync({ running: true, synced: 0, remaining: 0, failed: 0 }); setMsg('');
    let synced = 0, failed = 0;
    try {
      for (let i = 0; i < 40; i++) {
        const r = await api.post('/admin/bpay/mapping/sync', { building: building || null, max_units: 30 });
        synced += r.synced; failed += r.failed.length;
        setSync({ running: r.remaining > 0, synced, remaining: r.remaining, failed });
        await load();
        if (r.remaining <= 0) break;
      }
    } catch (e: any) { setMsg(errText(e, 'Үлдэгдэл шинэчлэх алдаа')); setSync(s => s ? { ...s, running: false } : null); }
  };

  const load = useCallback(async () => {
    const r = await api.get(`/admin/bpay/mapping${building ? `?building=${encodeURIComponent(building)}` : ''}`);
    setData(r);
  }, [building]);
  useEffect(() => { load().catch(e => setMsg(errText(e, 'Ачаалахад алдаа'))); }, [load]);

  const loadCands = async (u: Row) => {
    setOpen(u.unit_id);
    if (cands[u.unit_id] && cands[u.unit_id] !== 'loading') return;
    setCands(c => ({ ...c, [u.unit_id]: 'loading' }));
    try { const r = await api.get(`/admin/bpay/mapping/${u.unit_id}/candidates`); setCands(c => ({ ...c, [u.unit_id]: r.candidates })); }
    catch (e: any) { setCands(c => ({ ...c, [u.unit_id]: { error: errText(e, 'Алдаа') } })); }
  };
  const link = async (u: Row, c: Cand) => {
    setBusy(u.unit_id); setMsg('');
    try { await api.put(`/admin/bpay/mapping/${u.unit_id}`, { customer_id: c.customer_id, customer_code: c.customer_code, customer_name: c.customer_name }); setOpen(null); await load(); }
    catch (e: any) { setMsg(errText(e, 'Холбож чадсангүй')); } finally { setBusy(null); }
  };
  const unlink = async (u: Row) => {
    setBusy(u.unit_id);
    try { await api.delete(`/admin/bpay/mapping/${u.unit_id}`); setCands(c => { const n = { ...c }; delete n[u.unit_id]; return n; }); await load(); }
    catch (e: any) { setMsg(errText(e, 'Салгаж чадсангүй')); } finally { setBusy(null); }
  };
  const runAuto = async () => {
    setAuto({ running: true, linked: 0, ambiguous: 0, notfound: 0, remaining: 0 }); setMsg('');
    let acc = { linked: 0, ambiguous: 0, notfound: 0 };
    try {
      for (let i = 0; i < 40; i++) {
        const r = await api.post('/admin/bpay/mapping/auto', { building: building || null, max_doors: 30 });
        acc = { linked: acc.linked + r.linked.length, ambiguous: acc.ambiguous + r.ambiguous.length, notfound: acc.notfound + r.notfound.length };
        setAuto({ running: r.remaining_doors > 0, ...acc, remaining: r.remaining_doors });
        await load();
        if (r.remaining_doors <= 0) break;
      }
    } catch (e: any) { setMsg(errText(e, 'Авто-холболт алдаа')); setAuto(a => a ? { ...a, running: false } : null); }
  };

  const items = (data?.items || []).filter(u => filter === 'all' ? true : filter === 'linked' ? u.linked : !u.linked);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <button onClick={() => navigate('/units')} className="sh-btn sh-btn--ghost" style={{ minHeight: 40, padding: '0 8px' }}><Icon name="arrow-left" size={18} />Орон сууц</button>
        <div style={{ flex: 1, minWidth: 200 }}>
          <h1 style={{ fontSize: 22, fontWeight: 700, margin: 0 }}>Төрийн банкны хэрэглэгч холбох</h1>
          <div style={{ fontSize: 13, color: 'var(--muted)' }}>{data?.address || '—'} · {data ? `${data.linked} / ${data.total} айл холбогдсон` : ''}</div>
        </div>
        <Button variant="secondary" icon="wallet" loading={!!sync?.running} onClick={runSync} disabled={!data?.configured || !data?.linked}>Үлдэгдэл шинэчлэх</Button>
        <Button variant="secondary" icon="refresh" loading={!!auto?.running} onClick={runAuto} disabled={!data?.configured}>Авто-холбох{building ? ` (${building})` : ''}</Button>
      </div>

      {data && !data.configured && <Note tone="warn">Эхлээд Тохиргоо → СӨХ тохиргоо → «Төрийн банкны хаяг»-аа сонгоно уу.</Note>}
      {msg && <Note tone="danger" icon="alert">{msg}</Note>}
      {sync && <Note>{sync.running ? `Төрийн банкнаас үлдэгдэл татаж байна… (үлдсэн ${sync.remaining})` : 'Үлдэгдэл шинэчлэгдсэн.'} Шинэчилсэн <b>{sync.synced}</b>{sync.failed ? <> · алдаа <b>{sync.failed}</b></> : null}</Note>}
      {auto && (
        <Note>
          {auto.running ? `Холбож байна… (үлдсэн ${auto.remaining} тоот, BPay хязгаар 60 хүсэлт/мин)` : 'Авто-холболт дууссан.'} Холбосон <b>{auto.linked}</b> · олон таарсан (гараар сонгох) <b>{auto.ambiguous}</b> · банкинд олдсонгүй <b>{auto.notfound}</b>
        </Note>
      )}

      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
        <Select value={building} onChange={e => setBuilding(e.target.value)} style={{ width: 180 }}><option value="">Бүх байр</option>{(data?.buildings || []).map(b => <option key={b} value={b}>{b || '(байргүй)'}</option>)}</Select>
        <div className="sh-seg">
          {([['all', 'Бүгд'], ['unlinked', 'Холбогдоогүй'], ['linked', 'Холбогдсон']] as const).map(([k, l]) => (
            <button key={k} type="button" className={`sh-chip sh-chip--filter ${filter === k ? 'is-on' : ''}`} aria-pressed={filter === k} onClick={() => setFilter(k)}>{l}</button>
          ))}
        </div>
      </div>

      {!data ? <div className="sh-card"><Skeleton h={40} /><Skeleton h={40} style={{ marginTop: 8 }} /><Skeleton h={40} style={{ marginTop: 8 }} /></div> : items.length === 0 ? <Empty icon="building" title="Айл алга" text="Шүүлтүүрээ өөрчилнө үү." /> : (
        <div className="sh-card" style={{ padding: 0, overflowX: 'auto' }}>
          <table className="sh-table" style={{ width: '100%' }}>
            <thead><tr><th>Байр</th><th>Тоот</th><th>Эзэмшигч (манай)</th><th>Төрийн банкны хэрэглэгч</th><th>Код</th><th style={{ textAlign: 'right' }}>СӨХ үлдэгдэл (ТБ)</th><th style={{ textAlign: 'right' }}>Үйлдэл</th></tr></thead>
            <tbody>
              {items.map(u => (
                <React.Fragment key={u.unit_id}>
                  <tr>
                    <td>{u.building}</td>
                    <td className="tnum" style={{ fontWeight: 600 }}>{u.unit_number}</td>
                    <td style={{ color: u.owner_name ? 'var(--ink)' : 'var(--muted)' }}>{u.owner_name || '—'}</td>
                    <td>{u.linked ? <Chip tone="paid" icon="check">{u.bpay_customer_name || 'Холбогдсон'}</Chip> : <Chip tone="new">Холбогдоогүй</Chip>}</td>
                    <td className="tnum" style={{ color: 'var(--muted)', fontSize: 12 }}>{u.bpay_customer_ref || '—'}</td>
                    <td className="tnum" style={{ textAlign: 'right', fontWeight: 600, color: u.bpay_soh_due > 0 ? 'var(--red)' : 'var(--muted)' }} title={u.bpay_synced_at ? `Шинэчлэгдсэн ${new Date(u.bpay_synced_at).toLocaleString('mn-MN')}` : 'Шинэчлэгдээгүй'}>{u.linked ? (u.bpay_synced_at ? `${u.bpay_soh_due.toLocaleString('mn-MN').replace(/,/g, ' ')} ₮` : '—') : ''}</td>
                    <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                      <Button size="sm" variant="secondary" onClick={() => open === u.unit_id ? setOpen(null) : loadCands(u)}>{u.linked ? 'Солих' : 'Сонгох'}</Button>
                      {u.linked && <Button size="sm" variant="ghost" loading={busy === u.unit_id} onClick={() => unlink(u)} style={{ marginLeft: 6, color: 'var(--red)' }}>Салгах</Button>}
                    </td>
                  </tr>
                  {open === u.unit_id && (
                    <tr><td colSpan={7} style={{ background: 'var(--bg)', padding: '10px 16px' }}>
                      {cands[u.unit_id] === 'loading' || !cands[u.unit_id] ? <div style={{ fontSize: 13, color: 'var(--muted)' }}>Төрийн банкнаас хайж байна…</div>
                        : 'error' in (cands[u.unit_id] as any) ? <div className="sh-error"><Icon name="alert" size={14} />{(cands[u.unit_id] as any).error}</div>
                        : (cands[u.unit_id] as Cand[]).length === 0 ? <div style={{ fontSize: 13, color: 'var(--amber)' }}>{u.door} тоотод Төрийн банкны бүртгэл олдсонгүй — тоот банкинд өөр дугаартай байж магадгүй.</div>
                        : (
                          <div style={{ display: 'grid', gap: 8 }}>
                            <div style={{ fontSize: 12, color: 'var(--muted)' }}>{u.door} тоотод бүртгэлтэй {(cands[u.unit_id] as Cand[]).length} хэрэглэгч. {u.owner_name ? 'Нэрээр таарсан нь тодруулагдсан.' : 'Эзэмшигчийн нэр оруулбал автоматаар тааруулна.'}</div>
                            {(cands[u.unit_id] as Cand[]).map(c => (
                              <div key={c.customer_id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '8px 12px', borderRadius: 'var(--r-md)', border: `1.5px solid ${c.name_match ? 'var(--green)' : 'var(--line)'}`, background: 'var(--card)', opacity: c.taken_by ? .55 : 1 }}>
                                <span style={{ width: 34, height: 34, borderRadius: '50%', background: 'var(--green-soft)', color: 'var(--green)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700 }}>{(c.customer_name || '?').trim().charAt(0)}</span>
                                <div style={{ flex: 1, minWidth: 0 }}>
                                  <div style={{ fontWeight: 600 }}>{c.customer_name || 'Нэргүй'} {c.name_match && <Chip tone="paid">нэр таарсан</Chip>} {c.taken_by && <Chip tone="progress">{c.taken_by} тоотод холбогдсон</Chip>}</div>
                                  <div className="tnum" style={{ fontSize: 12, color: 'var(--muted)' }}>{c.address} · {c.customer_code}</div>
                                </div>
                                <Button size="sm" disabled={!!c.taken_by || u.bpay_customer_id === c.customer_id} loading={busy === u.unit_id} onClick={() => link(u, c)}>{u.bpay_customer_id === c.customer_id ? 'Холбогдсон' : 'Холбох'}</Button>
                              </div>
                            ))}
                          </div>
                        )}
                    </td></tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
